import type {
  ParsedQuery,
  BobResult,
  MailKontext,
  Counts,
  Draft,
  Mailbox,
  MailboxCreate,
  MailboxPatch,
  Me,
  Signature,
  ThreadDetail,
  ThreadListItem,
  View,
  Bedingungen,
  Regel,
} from '@mail/shared';

export type SearchResponse = {
  q: string;
  parsed: ParsedQuery;
  threads: ThreadListItem[];
  attachments: Array<{
    id: number;
    filename: string;
    mime: string;
    size: number;
    threadId: number;
    subject: string;
    date: string | null;
    mailboxId: number;
  }>;
  institutions: Array<{ id: string; name: string; n: number; crmUrl: string }>;
  facets: {
    status: Record<string, number>;
    mailbox: Record<string, number>;
    institution: Array<{ id: string; name: string; n: number }>;
    role: Record<string, number>;
    assigned: Record<string, number>;
    month: Record<string, number>;
  };
  total: number;
};

export class Unauthorized extends Error {}
export class ApiError extends Error {
  constructor(
    msg: string,
    public status: number,
  ) {
    super(msg);
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch('/api' + path, {
    credentials: 'same-origin',
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (r.status === 401) throw new Unauthorized();
  const j = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new ApiError(j.error ?? `HTTP ${r.status}`, r.status);
  return j;
}
const post = <T>(path: string, body?: unknown) =>
  req<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });

export type CrmInfo = {
  available: boolean;
  reason?: string;
  from?: string;
  kontext?: MailKontext | null;
  error?: string | null;
  link?: { institutionId: string; name: string | null; by: string; confirmed: boolean } | null;
  related?: Array<ThreadListItem & { repliedBy: string | null }>;
};

export const api = {
  me: () => req<Me & { via: string }>('/auth/me'),
  login: (user: string, password: string) => post('/auth/login', { user, password }),
  logout: () => post('/auth/logout'),
  mailboxes: () => req<Mailbox[]>('/mailboxes'),
  mailboxCreate: (body: MailboxCreate) => post<Mailbox>('/mailboxes', body),
  mailboxPatch: (id: number, body: MailboxPatch) =>
    req<Mailbox>(`/mailboxes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  mailboxDelete: (id: number) => req<{ ok: boolean }>(`/mailboxes/${id}`, { method: 'DELETE' }),
  counts: (mailbox?: number | null) =>
    req<Counts>('/counts' + (mailbox ? `?mailbox=${mailbox}` : '')),
  signatures: () => req<Signature[]>('/signatures'),
  threads: (q: {
    mailbox?: number | null;
    view?: View;
    q?: string;
    sort?: string;
    before?: string;
  }) => {
    const p = new URLSearchParams();
    if (q.sort) p.set('sort', q.sort);
    if (q.before) p.set('before', q.before);
    if (q.mailbox) p.set('mailbox', String(q.mailbox));
    if (q.view) p.set('view', q.view);
    if (q.q) p.set('q', q.q);
    return req<{ threads: ThreadListItem[] }>('/threads?' + p.toString());
  },
  thread: (id: number) => req<ThreadDetail>(`/threads/${id}`),
  commandRetry: (id: number) => post<{ ok: boolean }>(`/commands/${id}/retry`),
  seen: (id: number, seen: boolean) => post(`/threads/${id}/seen`, { seen }),
  done: (id: number) => post(`/threads/${id}/done`),
  reopen: (id: number) => post(`/threads/${id}/reopen`),
  later: (id: number, preset: string) =>
    post<{ until: string }>(`/threads/${id}/later`, { preset }),
  wait: (id: number, days: number | null) => post(`/threads/${id}/wait`, { days }),
  parallel: (id: number) => req<{ threads: ThreadListItem[] }>(`/threads/${id}/parallel`),
  scheduled: (id: number) =>
    req<{
      scheduled: Array<{ commandId: number; draftId: string; at: string; by: string | null }>;
    }>(`/threads/${id}/scheduled`),
  pin: (id: number, pinned: boolean) => post(`/threads/${id}/pin`, { pinned }),
  assign: (id: number, to: string | null) => post(`/threads/${id}/assign`, { to }),
  category: (id: number, category: string, scope: 'thread' | 'sender' | 'domain', block = false) =>
    post(`/threads/${id}/category`, { category, scope, block }),
  crm: (id: number, frisch = false) =>
    req<CrmInfo>(`/threads/${id}/crm${frisch ? '?frisch=1' : ''}`),
  crmLink: (
    id: number,
    body: {
      institutionId: string;
      name?: string;
      contactId?: string;
      role?: string;
      scope?: 'thread' | 'email' | 'domain';
    },
  ) => post(`/threads/${id}/crm-link`, body),
  crmUnlink: (id: number) => req(`/threads/${id}/crm-link`, { method: 'DELETE' }),
  search: (q: string) => req<SearchResponse>('/search?q=' + encodeURIComponent(q)),
  drafts: () => req<{ drafts: Draft[] }>('/drafts'),
  draft: (id: string) => req<Draft>(`/drafts/${id}`),
  newDraft: (body: {
    mailboxId: number;
    threadId?: number | null;
    to?: Array<{ address: string; name?: string }>;
    subject?: string;
  }) => post<Draft>('/drafts', body),
  replyDraft: (messageId: number, mode: 'reply' | 'replyAll' | 'forward') =>
    post<Draft>('/drafts/reply', { messageId, mode }),
  saveDraft: (
    id: string,
    body: Partial<
      Pick<Draft, 'to' | 'cc' | 'bcc' | 'subject' | 'html' | 'mailboxId' | 'attachmentRefs'>
    >,
    sync = true,
    init?: RequestInit,
  ) =>
    req(`/drafts/${id}${sync ? '' : '?sync=0'}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...init,
    }),
  deleteDraft: (id: string) => req(`/drafts/${id}`, { method: 'DELETE' }),
  send: (id: string, thenDone: boolean, sendAt?: string) =>
    post<{ undoUntil: string; sendAt: string }>(`/drafts/${id}/send`, {
      thenDone,
      ...(sendAt ? { sendAt } : {}),
    }),
  undo: (id: string) => post(`/drafts/${id}/undo`),
  signatureSave: (id: number, html: string) =>
    req(`/signatures/${id}`, { method: 'PUT', body: JSON.stringify({ html }) }),
  bob: (body: { threadId: number; draftId?: string | null; anweisung: string; html?: string }) =>
    post<BobResult>('/bob/entwurf', body),
  bobKarte: (body: { token: string; eingaben?: Record<string, unknown>; titel?: string }) =>
    post<{ ok: boolean; meldung?: string; fehler?: string }>('/bob/karte', body),
  bobFrage: (frage: string) =>
    post<BobResult & { threadId: number | null }>('/bob/frage', { frage }),
  bobZusammenfassung: (threadId: number) =>
    post<{ ok: boolean; text: string }>('/bob/zusammenfassung', { threadId }),
  // Regeln (Phase 2.C)
  regeln: () => req<RegelnResponse>('/regeln'),
  regelSave: (r: Regel) => post<RegelRow>('/regeln', r),
  regelDelete: (id: number) => req<{ ok: boolean }>(`/regeln/${id}`, { method: 'DELETE' }),
  regelProbelauf: (bedingungen: Bedingungen, tage = 30) =>
    post<Probelauf>('/regeln/probelauf', { bedingungen, tage }),
  regelProtokoll: () => req<{ eintraege: ProtokollEintrag[] }>('/regeln/protokoll'),
  regelRueckgaengig: (id: number) => post<{ ok: boolean }>(`/regeln/protokoll/${id}/rueckgaengig`),
  freigabeAdd: (address: string, label?: string) =>
    post<{ ok: boolean }>('/regeln/freigabeliste', { address, label }),
  freigabeDelete: (id: number) =>
    req<{ ok: boolean }>(`/regeln/freigabeliste/${id}`, { method: 'DELETE' }),
  regelBob: (text: string) =>
    post<{ regel: Regel; hinweis: string | null; text: string }>('/regeln/bob', { text }),
  // Flow & Kalender (Phase 2.D)
  flowStatus: () => req<{ available: boolean; url: string }>('/flow/status'),
  flowAufgaben: () => req<FlowAufgaben>('/flow/aufgaben'),
  flowAufgabeAnlegen: (body: {
    title: string;
    projectId?: number;
    priority?: FlowPrio;
    dueDate?: string | null;
  }) => post<{ ok: boolean; task: { id: string } }>('/flow/aufgaben', body),
  flowAufgabeAendern: (
    id: string,
    body: { title?: string; status?: FlowStatus; priority?: FlowPrio; dueDate?: string | null },
  ) =>
    req<{ ok: boolean }>(`/flow/aufgaben/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  flowInbox: () =>
    req<{ available: boolean; items: FlowInboxItem[]; error?: string }>('/flow/inbox'),
  flowInboxAnlegen: (body: { text: string; priority?: FlowPrio }) =>
    post<{ ok: boolean; item: FlowInboxItem }>('/flow/inbox', body),
  flowInboxAendern: (id: number, body: { text?: string; priority?: FlowPrio }) =>
    req<{ ok: boolean; item: FlowInboxItem }>(`/flow/inbox/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  flowInboxLoeschen: (id: number) =>
    req<{ ok: boolean }>(`/flow/inbox/${id}`, { method: 'DELETE' }),
  flowIcal: () => req<{ url: string | null; error?: string }>('/flow/ical'),
  flowKalender: (from: string, to: string) =>
    req<KalenderDaten>(
      `/flow/kalender?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  flowTerminAnlegen: (body: { title: string; start: string; end: string; allDay?: boolean }) =>
    post<{ ok: boolean }>('/flow/termine', body),
  flowTerminAendern: (
    id: number,
    body: { title?: string; start?: string; end?: string; allDay?: boolean },
  ) => req<{ ok: boolean }>(`/flow/termine/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  flowTerminLoeschen: (id: number) =>
    req<{ ok: boolean }>(`/flow/termine/${id}`, { method: 'DELETE' }),
  flowAufgabe: (
    threadId: number,
    body: { title: string; dueDate?: string | null; projectId?: number | null; notiz?: string },
  ) =>
    post<{ ok: boolean; task: { id: string; title: string; dueDate: string | null }; url: string }>(
      `/threads/${threadId}/flow-aufgabe`,
      body,
    ),
  flowTermin: (
    threadId: number,
    body: { title: string; start: string; end: string; allDay?: boolean },
  ) =>
    post<{ ok: boolean; event: { id: number; title: string; start: string } }>(
      `/threads/${threadId}/flow-termin`,
      body,
    ),
  // Team (Phase 3): Notizen, Kollisionsschutz, Gesprächsnotiz, Konto, Vorschlag übernehmen, Lernschleife
  notizen: (threadId: number) => req<{ notizen: NotizRow[] }>(`/threads/${threadId}/notizen`),
  notizAnlegen: (threadId: number, text: string) =>
    post<{ ok: boolean; notiz: NotizRow }>(`/threads/${threadId}/notizen`, { text }),
  notizLoeschen: (threadId: number, id: number) =>
    req<{ ok: boolean }>(`/threads/${threadId}/notizen/${id}`, { method: 'DELETE' }),
  presence: (threadId: number, editing: boolean) =>
    post<{ ok: boolean; andere: string[] }>(`/threads/${threadId}/presence`, { editing }),
  presenceLesen: (threadId: number) => req<{ andere: string[] }>(`/threads/${threadId}/presence`),
  gespraechsnotiz: (
    threadId: number,
    body: { text: string; dauerMin?: number; erinnerungTage?: number },
  ) => post<{ ok: boolean }>(`/threads/${threadId}/gespraechsnotiz`, body),
  konto: () => req<Konto>('/konto'),
  kontoSpeichern: (body: {
    absentUntil?: string | null;
    absentNote?: string | null;
    substitute?: 'till' | 'lars' | 'maren' | null;
    bobVorschlaege?: boolean;
  }) => req<{ ok: boolean }>('/konto', { method: 'PUT', body: JSON.stringify(body) }),
  uebernehmen: (draftId: string) =>
    post<{ ok: boolean; draftId: string }>(`/drafts/${draftId}/uebernehmen`),
  lernschleife: () =>
    req<{ laeuft: boolean; offeneFaelle: number; wochen: LernWoche[] }>('/lernschleife'),
  lernschleifeAuswerten: () => post<{ ok: boolean; laeuft: boolean }>('/lernschleife/auswerten'),
  // Termin-Akte, Fragen der Woche, Push (2.E)
  terminAkte: (eventId: string, frisch = false) =>
    req<TerminAkte>(
      `/termin-akte?event=${encodeURIComponent(eventId)}${frisch ? '&frisch=1' : ''}`,
    ),
  fragenDerWoche: () => req<{ laeuft: boolean; wochen: FragenWoche[] }>('/fragen-der-woche'),
  fragenAuswerten: () => post<{ ok: boolean; laeuft: boolean }>('/fragen-der-woche/auswerten'),
  pushVapid: () => req<{ publicKey: string | null; abos: string[] }>('/push/vapid'),
  pushSubscribe: (body: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    userAgent?: string;
  }) => post<{ ok: boolean }>('/push/subscribe', body),
  pushUnsubscribe: (endpoint: string) =>
    req<{ ok: boolean }>('/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
  pushTest: () => post<{ ok: boolean; gesendet: number }>('/push/test'),
  // Empfängerwahl (2.A)
  adressen: (q: string) => req<{ adressen: Adresse[] }>('/adressen?q=' + encodeURIComponent(q)),
  adressenZuletzt: () => req<{ crm: Adresse[]; verlauf: Adresse[] }>('/adressen/zuletzt'),
  // Textbausteine und Anhänge (2.A)
  bausteine: (threadId: number | null) =>
    req<{ bausteine: BausteinRow[]; buchungslink: string | null; werte: Record<string, string> }>(
      threadId ? `/threads/${threadId}/bausteine` : '/bausteine',
    ),
  crmAnhaenge: (threadId: number) =>
    req<{ vorschlaege: Array<{ art: 'flyer'; eventId: string; titel: string; hinweis: string }> }>(
      `/threads/${threadId}/crm-anhaenge`,
    ),
  draftAnhaenge: (id: string) =>
    req<{ anhaenge: AnhangRow[]; attachmentRefs: number[] }>(`/drafts/${id}/anhaenge`),
  draftAnhangUpload: async (id: string, file: File) => {
    const fd = new FormData();
    fd.append('datei', file, file.name);
    const r = await fetch(`/api/drafts/${id}/anhaenge`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
    const j = (await r.json().catch(() => ({}))) as {
      error?: string;
      anhang: AnhangRow;
      attachmentRefs: number[];
    };
    if (!r.ok) throw new ApiError(j.error ?? `HTTP ${r.status}`, r.status);
    return j;
  },
  draftAnhangCrm: (id: string, body: { art: 'flyer'; eventId: string }) =>
    post<{ ok: boolean; anhang: AnhangRow; attachmentRefs: number[] }>(
      `/drafts/${id}/anhaenge/crm`,
      body,
    ),
  draftAnhangDelete: (id: string, att: number) =>
    req<{ ok: boolean; attachmentRefs: number[] }>(`/drafts/${id}/anhaenge/${att}`, {
      method: 'DELETE',
    }),
  crmErinnerung: (threadId: number, body: { titel: string; faelligAm: string; fuer?: string }) =>
    post<{ ok: boolean }>(`/threads/${threadId}/crm-erinnerung`, body),
};

export type NotizRow = { id: number; user: string; text: string; am: string };
export type Konto = {
  konto: {
    absentUntil: string | null;
    absentNote: string | null;
    substitute: string | null;
    bobVorschlaege: boolean;
  } | null;
  team: Array<{
    key: string;
    name: string;
    abwesend: boolean;
    bis: string | null;
    vertretung: string | null;
  }>;
};
export type LernWoche = {
  woche: string;
  faelle: number;
  starkGeaendert: number;
  kostenEur: number;
  erzeugtAm: string;
  haeufigeAnweisungen: Array<{ anweisung: string; anzahl: number }>;
  vorschlaege: Array<{
    art: 'neues_kapitel' | 'kapitel_aendern' | 'ton' | 'baustein';
    kapitel: string | null;
    titel: string;
    text: string;
    begruendung: string;
    beispiele: Array<{ threadId: number | null; anweisung: string | null }>;
  }>;
};
export type TerminAkte = {
  ok: boolean;
  gecacht: boolean;
  eventId: string;
  einrichtung: { id: string; name: string };
  datum: string;
  markdown: string;
  mails: number;
  threadIds: number[];
  erzeugtAm: string;
};
export type FragenWoche = {
  woche: string;
  von: string;
  bis: string;
  mails: number;
  mitFragen: number;
  kostenEur: number;
  erzeugtAm: string;
  themen: Array<{
    thema: string;
    anzahl: number;
    vorwoche: number | null;
    beispiele: Array<{ threadId: number; betreff: string }>;
    handbuch: { deckt_ab: boolean; kapitel: string | null; hinweis: string };
    vorschlag: { art: 'handbuch' | 'faq' | 'zeitleiste' | 'keiner'; text: string };
  }>;
};
export type Adresse = {
  address: string;
  name: string;
  quelle: 'verlauf' | 'crm' | 'crm_zuletzt';
  einrichtung?: string | null;
  rolle?: string | null;
  hinweis?: string | null;
};
export type BausteinRow = {
  key: string;
  titel: string;
  gruppe: string;
  kapitel: string | null;
  text: string;
  html: string;
  pruefen: number;
};
export type AnhangRow = {
  id: number;
  filename: string;
  mime: string;
  size: number;
  quelle: string;
};

export type KalenderDaten = {
  available: boolean;
  error: string | null;
  users: Array<{ id: number; email: string; name: string; initials: string; role: string }>;
  events: Array<{
    id: number;
    title: string;
    type: 'meeting' | 'task' | 'deadline' | 'focus';
    startDatetime: string;
    endDatetime: string;
    allDay: boolean;
    ownerId: number | null;
    attendeeIds: number[];
  }>;
  tasks: Array<{
    id: string;
    title: string;
    dueDate: string | null;
    status: string;
    assigneeId: number | null;
    externalUrl?: string | null;
  }>;
  crm: Array<{
    id: string;
    datum: string;
    name: string;
    einrichtung: { id: string; name: string } | null;
    typ: string;
    modell: string;
    status: string;
    region: string;
    team: string[];
    crmUrl: string;
  }>;
};
export type FlowPrio = 'low' | 'medium' | 'high' | 'urgent';
export type FlowStatus = 'todo' | 'in_progress' | 'done';
export type FlowAufgabe = {
  id: string;
  title: string;
  dueDate: string | null;
  faelligHeute: boolean;
  status: FlowStatus;
  priority: FlowPrio;
  externalUrl: string | null;
  source: string | null;
  projectId: number;
  projekt: string | null;
  projektFarbe: string | null;
};
export type FlowInboxItem = {
  id: number;
  ownerId: number;
  text: string;
  priority: FlowPrio;
  createdAt: string;
  updatedAt: string;
};
export type FlowAufgaben = {
  available: boolean;
  aufgaben: FlowAufgabe[];
  projekte: Array<{ id: number; name: string; color: string }>;
  error?: string;
};

export type RegelRow = Regel & {
  id: number;
  erstelltVon: string | null;
  geaendertVon: string | null;
  updatedAt: string;
};
export type RegelnResponse = {
  regeln: RegelRow[];
  freigabeliste: Array<{ id: number; address: string; label: string | null }>;
  admin: boolean;
};
export type Probelauf = {
  geprueft: number;
  tage: number;
  treffer: Array<{
    id: number;
    threadId: number | null;
    von: string;
    betreff: string;
    datum: string;
    bob: string[];
  }>;
  knapp: Array<{
    id: number;
    threadId: number | null;
    von: string;
    betreff: string;
    datum: string;
    fehlt: string;
  }>;
  hinweis: string | null;
};
export type ProtokollEintrag = {
  id: number;
  ruleName: string;
  ruleId: number | null;
  messageId: number | null;
  threadId: number | null;
  betreff: string;
  von: string;
  ergebnis: string;
  begruendung: string | null;
  aktionen: Array<{ typ: string; ok: boolean; info?: string }>;
  easybill: string | null;
  rueckgaengigMoeglich: boolean;
  rueckgaengigGemacht: string | null;
  am: string;
};
